import type { Hex, Address } from 'viem';

// Boost sponsorship endpoint. A JSON-RPC passthrough that geo-routes between
// sponsorship upstreams; it serves the bundler, ERC-7677 paymaster and
// `pimlico_getUserOperationGasPrice` methods the gasless submitter uses.
// Replaces `/v1/pimlico/sponsor`, the legacy single-provider alias.
export const DEFAULT_SPONSOR_URL = 'https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161';
export const DEFAULT_SPONSOR_URL_TESTNET = 'https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=421614';

/** @deprecated Use `DEFAULT_SPONSOR_URL`. Kept so existing imports keep resolving. */
export const DEFAULT_PIMLICO_URL = DEFAULT_SPONSOR_URL;
/** @deprecated Use `DEFAULT_SPONSOR_URL_TESTNET`. */
export const DEFAULT_PIMLICO_URL_TESTNET = DEFAULT_SPONSOR_URL_TESTNET;

export type ClientMode =
  | 'self-self'
  | 'self-gasless'
  | 'delegated-self'
  | 'delegated-gasless';

// ─────────────────────────────────────────────────────────────────────────────
// Named factory param types (preferred API)
// ─────────────────────────────────────────────────────────────────────────────

/** Shared optional settings available in every mode. */
interface CommonParams {
  /** Use Arbitrum Sepolia testnet. Defaults to false (mainnet). */
  testnet?: boolean;
  /** Default slippage tolerance in basis points (e.g. 25 = 0.25%). Defaults to 25. */
  slippageBps?: number;
  /**
   * Optional builder fee sharing.
   * `feeBps` in basis points (e.g. 10 = 10 bps / 0.1%, 2.55 = 2.55 bps). Max 50 (0.5%), step 0.01 bps.
   */
  builder?: {
    address: Address;
    feeBps: number;
  };
  /**
   * Override the Ostium subgraph endpoint used by built-in read methods
   * (`getPairs`, `getOpenPositions`, etc.). Defaults to the mainnet or testnet
   * subgraph based on `testnet`.
   */
  subgraphUrl?: string;
  /**
   * Override the builder API base URL used for live prices, OHLC candles, and
   * the WebSocket stream. Defaults to `https://builder.prod.bedrock.ostium.io`.
   */
  builderApiUrl?: string;
  /**
   * Alchemy API key used by account confirmation streams for Arbitrum contract
   * log overlays. Can be overridden per `streamAccountUpdates()` call.
   */
  alchemyApiKey?: string;
}

export interface SelfSelfSubmitParams extends CommonParams {
  /** Private key of the trader's EOA. This account holds USDC, owns all positions, and pays gas. */
  traderPrivateKey: Hex;
  /** Arbitrum One (or Sepolia) RPC URL. */
  rpcUrl: string;
}

export interface SelfSelfBuildParams extends CommonParams {
  /** Trader EOA address. Used to build unsigned transactions without SDK submission. */
  traderAddress: Address;
  /** Optional Arbitrum RPC URL for reads/simulation. Defaults to the chain public RPC. */
  rpcUrl?: string;
}

/**
 * **Self + Self** — simplest mode.
 *
 * Your EOA is the trader: it holds USDC, owns all positions, and pays gas
 * for every transaction directly.
 */
export type SelfSelfParams = SelfSelfSubmitParams | SelfSelfBuildParams;

export interface SelfGaslessSubmitParams extends CommonParams {
  /** Private key of the trader's EOA. This account holds USDC and owns all positions. A Safe is derived from this key to relay trades gaslessly. */
  traderPrivateKey: Hex;
  /**
   * Known smart-account address for this key (from a previous client's
   * `getSmartAccountAddress()`). Skips the on-chain derivation call, making
   * construction network-free. The address is deterministic per key + chain.
   */
  safeAddress?: Address;
  /**
   * ERC-4337 bundler/paymaster RPC URL. Defaults to Ostium's boost sponsorship
   * endpoint, `https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161`,
   * which sponsors the operation upstream.
   *
   * Point it at your own Pimlico project to sponsor through your own account
   * instead: the submitter then attaches a Pimlico paymaster, applies
   * `sponsorshipPolicyId`, and uses Pimlico's fee quote and gas estimation.
   * The flow is selected from the URL, so no extra flag is needed.
   */
  pimlicoUrl?: string;
  /** Arbitrum RPC URL for reads/simulation. Defaults to public Arbitrum RPC when omitted. */
  rpcUrl?: string;
  /**
   * Pimlico gas-sponsorship policy id. Applies only when `pimlicoUrl` points at
   * your own Pimlico project — Ostium's endpoint enforces policy upstream, so
   * setting both throws `INVALID_CONFIG` rather than dropping the value.
   */
  sponsorshipPolicyId?: string;
}

export interface SelfGaslessBuildParams extends CommonParams {
  /** Trader EOA address that owns USDC and positions. */
  traderAddress: Address;
  /** Safe smart-account address that will submit delegated calls. */
  safeAddress: Address;
  /** Optional Arbitrum RPC URL for reads/simulation. Defaults to the chain public RPC. */
  rpcUrl?: string;
}

/**
 * **Self + Gasless** — your EOA owns everything; a Safe acts as a transparent gasless relay.
 *
 * Submit-capable clients derive the Safe from `traderPrivateKey`. Build-only
 * clients provide both `traderAddress` and `safeAddress`.
 */
export type SelfGaslessParams = SelfGaslessSubmitParams | SelfGaslessBuildParams;

export interface DelegatedSelfSubmitParams extends CommonParams {
  /** Private key of the delegate EOA. This account signs and pays gas for all transactions. */
  delegatePrivateKey: Hex;
  /**
   * Address of the trader this delegate acts for.
   * The trader must have called `setDelegate(delegateAddress)` on the trading contract.
   */
  traderAddress: Address;
  /** Arbitrum One (or Sepolia) RPC URL. */
  rpcUrl: string;
}

export interface DelegatedSelfBuildParams extends CommonParams {
  /** Address of the trader this delegate acts for. */
  traderAddress: Address;
  /** Delegate EOA address that will submit the transaction. */
  delegateAddress: Address;
  /** Optional Arbitrum RPC URL for reads/simulation. Defaults to the chain public RPC. */
  rpcUrl?: string;
}

/**
 * **Delegated + Self** — a dedicated delegate EOA signs and pays gas on behalf of a separate trader address.
 */
export type DelegatedSelfParams = DelegatedSelfSubmitParams | DelegatedSelfBuildParams;

export interface DelegatedGaslessSubmitParams extends CommonParams {
  /** Private key of the delegate EOA. A Safe smart account is derived from this key and submits UserOperations via Pimlico. */
  delegatePrivateKey: Hex;
  /**
   * Known smart-account address for the delegate key (from a previous client's
   * `getSmartAccountAddress()`). Skips the on-chain derivation call, making
   * construction network-free. The address is deterministic per key + chain.
   */
  safeAddress?: Address;
  /**
   * Address of the trader this delegate acts for.
   * The trader must have called `setDelegate(safeAddress)` on the trading contract,
   * where `safeAddress` is the Safe derived from `delegatePrivateKey`.
   */
  traderAddress: Address;
  /**
   * ERC-4337 bundler/paymaster RPC URL. Defaults to Ostium's boost sponsorship
   * endpoint, `https://builder.prod.bedrock.ostium.io/v1/sponsor?chainId=42161`,
   * which sponsors the operation upstream.
   *
   * Point it at your own Pimlico project to sponsor through your own account
   * instead: the submitter then attaches a Pimlico paymaster, applies
   * `sponsorshipPolicyId`, and uses Pimlico's fee quote and gas estimation.
   * The flow is selected from the URL, so no extra flag is needed.
   */
  pimlicoUrl?: string;
  /** Arbitrum RPC URL for reads/simulation. Defaults to public Arbitrum RPC when omitted. */
  rpcUrl?: string;
  /**
   * Pimlico gas-sponsorship policy id. Applies only when `pimlicoUrl` points at
   * your own Pimlico project — Ostium's endpoint enforces policy upstream, so
   * setting both throws `INVALID_CONFIG` rather than dropping the value.
   */
  sponsorshipPolicyId?: string;
}

export interface DelegatedGaslessBuildParams extends CommonParams {
  /** Address of the trader this delegate acts for. */
  traderAddress: Address;
  /** Delegate EOA address that owns the Safe. */
  delegateAddress: Address;
  /** Safe smart-account address that will submit delegated calls. */
  safeAddress: Address;
  /** Optional Arbitrum RPC URL for reads/simulation. Defaults to the chain public RPC. */
  rpcUrl?: string;
}

/**
 * **Delegated + Gasless** — a Safe derived from the delegate key submits sponsored UserOperations on behalf of the trader.
 */
export type DelegatedGaslessParams = DelegatedGaslessSubmitParams | DelegatedGaslessBuildParams;

// ─────────────────────────────────────────────────────────────────────────────
// Internal generic config
// ─────────────────────────────────────────────────────────────────────────────

export interface OstiumClientConfig extends CommonParams {
  mode?: ClientMode;
  privateKey?: Hex;
  traderAddress?: Address;
  delegateAddress?: Address;
  safeAddress?: Address;
  rpcUrl?: string;
  pimlicoUrl?: string;
  /**
   * Pimlico gas-sponsorship policy id. Applies only when `pimlicoUrl` points at
   * your own Pimlico project — Ostium's endpoint enforces policy upstream, so
   * setting both throws `INVALID_CONFIG` rather than dropping the value.
   */
  sponsorshipPolicyId?: string;
}

export function hasPrivateKey(config: OstiumClientConfig): config is OstiumClientConfig & { privateKey: Hex } {
  return typeof config.privateKey === 'string' && config.privateKey.length > 0;
}

export function resolveMode(config: OstiumClientConfig): ClientMode {
  if (config.mode) return config.mode;
  if (config.privateKey) {
    if (config.pimlicoUrl) return config.traderAddress ? 'delegated-gasless' : 'self-gasless';
    return config.traderAddress ? 'delegated-self' : 'self-self';
  }
  if (config.safeAddress && config.delegateAddress && config.traderAddress) return 'delegated-gasless';
  if (config.safeAddress && config.traderAddress) return 'self-gasless';
  if (config.delegateAddress && config.traderAddress) return 'delegated-self';
  if (config.traderAddress) return 'self-self';
  throw new Error('Unable to infer client mode from config');
}

export function isGasless(config: OstiumClientConfig): boolean {
  const mode = resolveMode(config);
  return mode === 'self-gasless' || mode === 'delegated-gasless';
}

export function isDelegated(config: OstiumClientConfig): boolean {
  const mode = resolveMode(config);
  return mode === 'delegated-self' || mode === 'delegated-gasless';
}
